// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { FormFieldClickable, FormFieldIcon } from "@insite/mobius/FormField";
import { IconWrapper } from "@insite/mobius/Icon";
import { getProp } from "@insite/mobius/utilities";
import { XngExpirationDatePickerStyle } from "@werner/App/Components/XngExpirationDatePicker";
import { DateTimePickerStyle } from "@werner/MobiusOverrides/DatePicker/DatePicker";
import { SelectedText } from "@werner/MobiusOverrides/DynamicDropdown/DynamicDropdown";
import { TokenExFrameWrapper } from "@werner/MobiusOverrides/TokenExFrame/TokenExFrame";
import { fontConfig } from "@werner/XDL/PreStyle/typography";
import { xBaseTheme } from "@werner/XDL/types";
import { color, ellipsis, xFirefoxOnly } from "@werner/XDL/Utilities";
import { css } from "styled-components";

export const PlaceholderSelector = `
        &::placeholder,
        &::-webkit-input-placeholder
`;

export const floatingLabel = css`
    label {
        padding-top: 5px;
        top: 15px;
        font-size: 14px;
    }
`;

export const hidePlaceholderStyle = css`
    ${PlaceholderSelector} {
        opacity: 0;
    }
`;

export const showPlaceholderStyle = css`
    ${PlaceholderSelector} {
        opacity: 1;
        color: ${color("common.ui3")};
    }
`;

export default {
    defaultProps: {
        border: "rounded",
        sizeVariant: "default",
        mergeCss: true,
        labelProps: {
            mergeCss: true,
            size: 15,
            weight: 400,
            color: "common.ui3",
            css: css`
                cursor: text;
                width: inherit;
                max-width: calc(100% - 40px);
                position: absolute;
                z-index: 1;
                left: 20px;
                top: 50%;
                transform: translateY(-50%);
                transition: top 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
                text-align: left;
            `,
        },
        cssOverrides: {
            formField: css`
                & > label {
                    position: static;
                    transform: unset;
                }
                &:focus-within {
                    ${floatingLabel}
                    & textarea {
                        & ~ label {
                            top: 15px;
                            padding-top: 5px;
                            font-size: 14px;
                            ${xFirefoxOnly(`top: 16px;`)}
                        }
                    }
                }
            `,
            formInputWrapper: css`
                & select,
                & ${XngExpirationDatePickerStyle},
                & ${DateTimePickerStyle},
                & input:not(:placeholder-shown),
                & div[role="combobox"][data-has-value="true"] {
                    & ~ ${floatingLabel}
                }
                & textarea:not(:placeholder-shown) {
                    & ~ label {
                        top: 14px;
                        padding-top: 5px;
                        font-size: 14px;
                        ${xFirefoxOnly(`top: 15px;`)}
                    }
                }
                & textarea + label{
                    background-color: ${color("common.white")};
                }
                
                input::placeholder,
                textarea::placeholder {
                    transition: all ${getProp("theme.transition.duration.short")}ms ease-in-out;
                }
                

                &.has-label {
                    input:placeholder-shown,
                    textarea:placeholder-shown,
                    div[role="combobox"][data-has-value="false"] > input,
                    select {
                        ${hidePlaceholderStyle}
                    }
                    & > ${TokenExFrameWrapper} {
                        & +  ${floatingLabel}
                    }
                }
                
                input:focus:placeholder-shown,
                textarea:focus:placeholder-shown {
                    ${showPlaceholderStyle}
                }
               
                & > ${IconWrapper}, & > ${FormFieldClickable} {
                    z-index: 2;
                }
                   
            `,
        },
    },
} as xBaseTheme["formField"];
